import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const indicatorRef = useRef<HTMLDivElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sectionIds = ['features', 'angle-design', 'material', 'scenes', 'comparison', 'faq'];
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // アクティブインジケーターの位置を更新
  useEffect(() => {
    if (!navContainerRef.current || !indicatorRef.current) return;
    const activeEl = navContainerRef.current.querySelector(`[data-section="${activeSection}"]`) as HTMLElement | null;
    if (activeEl) {
      const containerRect = navContainerRef.current.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      indicatorRef.current.style.width = `${activeRect.width}px`;
      indicatorRef.current.style.left = `${activeRect.left - containerRect.left}px`;
      indicatorRef.current.style.opacity = '1';
    } else {
      indicatorRef.current.style.opacity = '0';
    }
  }, [activeSection, isScrolled]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { label: '特徴', href: '#features' },
    { label: '角度設計', href: '#angle-design' },
    { label: '素材', href: '#material' },
    { label: '活用シーン', href: '#scenes' },
    { label: '比較・仕様', href: '#comparison' },
    { label: 'FAQ', href: '#faq' },
  ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) {
      const navHeight = 64;
      const elementPosition = el.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - navHeight;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          isScrolled || isMobileMenuOpen
            ? 'bg-white/80 backdrop-blur-xl border-b border-black/[0.04] shadow-[0_1px_12px_rgba(0,0,0,0.06)]'
            : 'bg-transparent'
        }`}
      >
        <div
          className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-500 ${
            isScrolled ? 'py-2.5' : 'py-4'
          }`}
        >
          {/* ロゴ */}
          <Link to="/" className="flex items-center relative z-50 group cursor-pointer">
            <img
              src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/a6432ee4b37c265c1d5171353c491488.png"
              alt="Atelier Folio"
              className={`w-auto transition-all duration-500 group-hover:opacity-80 ${
                isScrolled ? 'h-8' : 'h-9'
              }`}
            />
          </Link>

          {/* デスクトップメニュー */}
          <div className="hidden lg:flex items-center gap-1" ref={navContainerRef}>
            {navItems.map((item) => {
              const sectionId = item.href.replace('#', '');
              const isActive = activeSection === sectionId;
              return (
                <a
                  key={item.href}
                  href={item.href}
                  data-section={sectionId}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }}
                  className={`relative px-3.5 py-2 text-[13px] font-medium rounded-lg transition-all duration-300 cursor-pointer ${
                    isScrolled
                      ? isActive
                        ? 'text-[#2C2C2C]'
                        : 'text-gray-500 hover:text-[#2C2C2C] hover:bg-black/[0.03]'
                      : isActive
                      ? 'text-white'
                      : 'text-white/70 hover:text-white hover:bg-white/[0.08]'
                  }`}
                >
                  {item.label}
                </a>
              );
            })}

            {/* スライドするアクティブインジケーター */}
            <div
              ref={indicatorRef}
              className={`absolute bottom-0 h-[2px] rounded-full transition-all duration-400 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
                isScrolled ? 'bg-[#C8956C]' : 'bg-white'
              }`}
              style={{ opacity: 0 }}
            />

            <div className={`w-px h-4 mx-2 ${isScrolled ? 'bg-gray-200' : 'bg-white/20'} transition-colors duration-500`} />

            <a
              href="https://lin.ee/d6PoCFQ6"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group relative ml-3 inline-flex items-center gap-2 px-5 py-2 bg-[#06C755] text-white text-[13px] font-semibold rounded-full transition-all duration-300 hover:shadow-[0_4px_20px_rgba(6,199,85,0.4)] hover:scale-[1.03] whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(6,199,85,0.25)]"
            >
              <svg className="w-4 h-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
              </svg>
              <span>Makuake優先案内を受取る</span>
              <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>
          </div>

          {/* ハンバーガーボタン */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-50 w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="メニューを開く"
          >
            <div className="w-5 h-4 flex flex-col justify-between">
              <span
                className={`block w-full h-[1.5px] rounded-full transition-all duration-400 ease-out origin-center ${
                  isMobileMenuOpen
                    ? 'bg-gray-800 rotate-45 translate-y-[7.25px]'
                    : isScrolled
                    ? 'bg-gray-800'
                    : 'bg-white'
                }`}
              />
              <span
                className={`block h-[1.5px] rounded-full transition-all duration-300 ${
                  isMobileMenuOpen
                    ? 'w-0 opacity-0 bg-gray-800'
                    : isScrolled
                    ? 'w-3/4 bg-gray-800 opacity-100'
                    : 'w-3/4 bg-white opacity-100'
                }`}
              />
              <span
                className={`block w-full h-[1.5px] rounded-full transition-all duration-400 ease-out origin-center ${
                  isMobileMenuOpen
                    ? 'bg-gray-800 -rotate-45 -translate-y-[7.25px]'
                    : isScrolled
                    ? 'bg-gray-800'
                    : 'bg-white'
                }`}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* モバイルオーバーレイ */}
      <div
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-400 lg:hidden ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* モバイルメニューパネル */}
      <div
        className={`fixed top-0 right-0 z-40 w-[300px] h-full bg-white/95 backdrop-blur-xl shadow-[-8px_0_30px_rgba(0,0,0,0.08)] transition-transform duration-400 ease-[cubic-bezier(0.32,0.72,0,1)] lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="pt-20 px-6 pb-8 h-full flex flex-col">
          <div className="flex-1 space-y-0.5 pt-4">
            {navItems.map((item, index) => {
              const sectionId = item.href.replace('#', '');
              const isActive = activeSection === sectionId;
              return (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.href);
                  }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-300 cursor-pointer group ${
                    isActive
                      ? 'text-[#2C2C2C] bg-[#F7F3EE]'
                      : 'text-gray-500 hover:text-[#2C2C2C] hover:bg-gray-50'
                  }`}
                  style={{
                    transitionDelay: isMobileMenuOpen ? `${index * 40}ms` : '0ms',
                    opacity: isMobileMenuOpen ? 1 : 0,
                    transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(16px)',
                    transition: 'all 0.4s cubic-bezier(0.32, 0.72, 0, 1)',
                  }}
                >
                  <span
                    className={`w-1 h-1 rounded-full transition-all duration-300 ${
                      isActive ? 'bg-[#C8956C] scale-150' : 'bg-gray-300 group-hover:bg-[#C8956C]'
                    }`}
                  />
                  <span className={`text-sm ${isActive ? 'font-semibold' : 'font-medium'}`}>
                    {item.label}
                  </span>
                  {isActive && (
                    <i className="ri-arrow-right-s-line text-[#C8956C] ml-auto text-base" />
                  )}
                </a>
              );
            })}
          </div>

          {/* モバイルCTA */}
          <div
            className="pt-5 border-t border-gray-100"
            style={{
              transitionDelay: isMobileMenuOpen ? `${(navItems.length + 1) * 40}ms` : '0ms',
              opacity: isMobileMenuOpen ? 1 : 0,
              transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(12px)',
              transition: 'all 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
            }}
          >
            <a
              href="https://lin.ee/d6PoCFQ6"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="group flex items-center justify-center gap-2 w-full py-3 bg-[#06C755] text-white text-sm font-semibold rounded-full hover:bg-[#05B34C] transition-all duration-300 hover:scale-[1.02] whitespace-nowrap cursor-pointer shadow-[0_2px_12px_rgba(6,199,85,0.3)] hover:shadow-[0_4px_20px_rgba(6,199,85,0.4)]"
            >
              <svg className="w-4 h-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
              </svg>
              <span>Makuake優先案内を受取る</span>
              <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
            </a>

            <a
              href="https://www.instagram.com/mever_jp/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center justify-center gap-2 mt-3 py-2 text-sm text-gray-400 hover:text-[#C8956C] transition-colors cursor-pointer"
            >
              <i className="ri-instagram-line text-base"></i>
              @mever_jp
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
